import {
  Cog6ToothIcon,
  PlusIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline"
import { useEffect, useMemo, useState } from "react"
import { Navigate, Route, Routes, useLocation } from "react-router-dom"
import { toast } from "react-toastify"
import { useTriggerMetaAdsQaMutation } from "~/ui-rtk/api/jobsApi"
import { useAppDispatch } from "~/ui-rtk/app/hooks"
import { adForgeActions } from "~/ui-rtk/app/slices/ad-forge.slice"
import { AgendaContextBanner } from "~/ui-rtk/components/features/agenda"
import MetaAdAccountGate from "~/ui-rtk/components/features/meta-ad-account-gate/MetaAdAccountGate"
import TabsGroup from "~/ui-rtk/components/ui/controls/TabsGroup/TabsGroup"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import { CONNECTORS_IDS } from "~/ui-rtk/constants/sources"
import { useCheckServiceIsConnected } from "~/ui-rtk/hooks/connected-services"
import usePartnershipTrackerAccess from "~/ui-rtk/hooks/partnership-tracker-access"
import { useStableNavigate } from "~/ui-rtk/utils/StableNavigateContext"
import AdForgePage from "../AdForge"
import AdsTab from "./components/AdsTab"
import CreativesTab from "./components/CreativesTab"
import PartnershipsTab from "./components/PartnershipsTab"
import SocialPostsTab from "./components/SocialPostsTab"

interface TabLink {
  label: string
  to: string
}

const BASE_TAB_LINKS: TabLink[] = [
  { label: "Creatives", to: brandRoutes.AD_BLASTER.CREATIVES },
  { label: "Ads", to: brandRoutes.AD_BLASTER.ADS },
  { label: "Social posts", to: brandRoutes.AD_BLASTER.SOCIAL_POSTS },
]

// Fourth tab — the partnership ads tracker (MD-5524). Behind its own PostHog
// flag while the Meta discovery sync + blast path roll out, so it's appended
// to the tab list only when the flag is on (see `usePartnershipTrackerAccess`).
const PARTNERSHIPS_TAB_LINK: TabLink = {
  label: "Partnerships",
  to: brandRoutes.AD_BLASTER.PARTNERSHIPS,
}

/**
 * AdBlaster — the launcher / library page.
 *
 *   • /creatives — every AdCreative saved by the company + the live Meta
 *     library. Source-of-truth for re-blasting.
 *   • /ads      — every Ad row for the company (SYNC + APP). Re-blastable.
 *   • /ad-forge — the editor, mounted as a nested route. Reached via the
 *     "+ AdForge" CTA on the header. AdForge has its own page chrome, so
 *     when we're on this sub-route we skip the AdBlaster title/tabs and
 *     render the editor full-bleed.
 *
 * Tab state lives in the URL so the page is deep-linkable and survives
 * back/forward navigation — same pattern as SettingsTabs.
 */
function AdBlasterPageInner() {
  const location = useLocation()
  const navigate = useStableNavigate()
  const dispatch = useAppDispatch()
  const [triggerMetaAdsQa, { isLoading: isQaStarting }] =
    useTriggerMetaAdsQaMutation()
  const { hasAccess: hasPartnershipAccess } = usePartnershipTrackerAccess()

  const tabLinks = useMemo(
    () =>
      hasPartnershipAccess
        ? [...BASE_TAB_LINKS, PARTNERSHIPS_TAB_LINK]
        : BASE_TAB_LINKS,
    [hasPartnershipAccess],
  )

  // Kick off the meta-ads-qa background job for the current company. The
  // server dedupes (one active run per company), so double-clicks just get
  // the existing job back. Findings land on the Jobs page + Slack.
  const handleCheckQa = async () => {
    try {
      await triggerMetaAdsQa().unwrap()
      toast.success(
        "Ads QA started — findings will appear on the Jobs page and in Slack.",
      )
    } catch {
      toast.error("Could not start the ads QA job. Please try again.")
    }
  }

  // Starting a brand-new creative from the "+ AdForge" CTA must not inherit a
  // prior session's picked/selected images, copy, source ad, or ad-set picks —
  // reset the editor slice to its initial state before entering. (The edit flow
  // enters via `?creative=…` from the Creatives tab and hydrates separately, so
  // it's unaffected.)
  const handleNewAdForge = () => {
    dispatch(adForgeActions.resetAdForge())
    navigate(brandRoutes.AD_BLASTER.AD_FORGE)
  }

  // AdForge has its own header + sidebar, so we hide the AdBlaster chrome
  // when the user navigates into the editor. Detected by URL prefix rather
  // than a route match so we don't have to plumb the active route through.
  const isAdForge = location.pathname.startsWith(
    brandRoutes.AD_BLASTER.AD_FORGE,
  )

  const activeIndex = useMemo(() => {
    const i = tabLinks.findIndex(link => location.pathname.startsWith(link.to))
    // `/ad-blaster` (root) lands on the first tab — see the redirect below.
    return i === -1 ? 0 : i
  }, [location.pathname, tabLinks])

  useEffect(() => {
    // Redirect bare `/ad-blaster` to the default tab so the URL always carries
    // the active sub-route. Done in an effect (rather than rendering <Navigate />
    // unconditionally) so tab clicks can use `navigate(...)` without fighting
    // a render-loop with the redirect.
    if (location.pathname === brandRoutes.AD_BLASTER.ROOT) {
      navigate(brandRoutes.AD_BLASTER.CREATIVES, { replace: true })
    }
  }, [location.pathname, navigate])

  const handleTabChange = (idx: number) => {
    const link = tabLinks[idx]
    if (link) navigate(link.to)
  }

  // Full-bleed AdForge — no header, no tabs, no padding. AdForge has its own
  // top-level layout and our wrapper padding would just compete with it.
  if (isAdForge) {
    return (
      <Routes>
        <Route path="ad-forge" element={<AdForgePage />} />
      </Routes>
    )
  }

  return (
    <div className="flex flex-col gap-system-md p-system-md">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-white">AdBlaster</h1>
        <div className="flex items-center gap-system-sm">
          <button
            type="button"
            onClick={handleCheckQa}
            disabled={isQaStarting}
            className="flex items-center gap-system-2xs rounded-md border border-system-blue-700 px-system-sm py-system-2xs text-sm font-medium text-system-gray-200 hover:bg-system-blue-700 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Check QA"
            title="QA recently-created Meta ads (CTA, copy, limits, typos)"
          >
            <ShieldCheckIcon className="h-4 w-4" />
            {isQaStarting ? "Starting…" : "Check QA"}
          </button>
          <button
            type="button"
            onClick={() => navigate(brandRoutes.SETTINGS.AD_BLASTER)}
            className="flex items-center rounded-md border border-system-blue-700 px-system-sm py-system-2xs text-sm font-medium text-system-gray-200 hover:bg-system-blue-700 hover:text-white"
            aria-label="Ad Blaster settings"
            title="AdForge defaults + Ads QA schedule (Settings → Ad Blaster)"
          >
            <Cog6ToothIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleNewAdForge}
            className="flex items-center gap-system-2xs rounded-md bg-system-blue-700 px-system-md py-system-2xs text-sm font-medium text-white hover:bg-system-blue-600"
          >
            <PlusIcon className="h-4 w-4" />
            AdForge
          </button>
        </div>
      </div>

      <AgendaContextBanner />

      <TabsGroup
        options={tabLinks.map(({ label }) => ({ label }))}
        index={activeIndex}
        onChange={handleTabChange}
      />

      <Routes>
        <Route path="creatives" element={<CreativesTab />} />
        <Route path="ads" element={<AdsTab />} />
        <Route path="social-posts" element={<SocialPostsTab />} />
        {/* Flag off → no route either, so a deep link falls through to the
         * catch-all redirect instead of rendering a hidden tab. */}
        {hasPartnershipAccess && (
          <Route path="partnerships" element={<PartnershipsTab />} />
        )}
        <Route
          path="*"
          element={<Navigate to={brandRoutes.AD_BLASTER.CREATIVES} replace />}
        />
      </Routes>
    </div>
  )
}

export default function AdBlasterPage() {
  // Ad-account selection lives at the AdBlaster level so the Creatives tab
  // (which lists Meta library entries), the Ads tab, and the nested AdForge
  // editor all share the same gate. The value isn't read here — the gate
  // resolves the saved account server-side and just renders children when
  // one is set.
  const [selectedAdAccountId, setSelectedAdAccountId] = useState<string | null>(
    null,
  )
  // AdBlaster is Meta OR TikTok: with TikTok Ads Management connected the
  // page is usable without a Meta ad account (TikTok ads list and re-blast),
  // so the Meta gate becomes optional instead of blocking the whole page.
  const isTikTokConnected = useCheckServiceIsConnected(
    CONNECTORS_IDS.TIKTOK_ADS_MANAGEMENT,
  )
  return (
    <MetaAdAccountGate
      title="Select Ad Account for AdBlaster"
      selectedAdAccountId={selectedAdAccountId}
      onSelect={setSelectedAdAccountId}
      allowWithoutAccount={isTikTokConnected}
    >
      <AdBlasterPageInner />
    </MetaAdAccountGate>
  )
}
